import React, { useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { ACTIVITIES, VIDEOS } from '../lib/butterstone';

const LazyVideo = ({ src, poster }) => {
  const ref = useRef(null);
  const [inview, setInview] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInview(true); el.play?.().catch(() => {}); } else { el.pause?.(); }
    }, { rootMargin: '200px' });
    obs.observe(el); return () => obs.disconnect();
  }, []);
  return (
    <video ref={ref} className="w-full h-full object-cover" muted loop playsInline poster={poster} preload="none">
      {inview && <source src={src} type="video/mp4" />}
    </video>
  );
};

const ActivitiesSection = () => {
  return (
    <section id="activities" className="section-pad bg-[#1E3226]" data-testid="activities-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="mb-12 md:flex items-end justify-between">
          <div>
            <span className="overline" style={{ color: '#AFC2A4' }}>Things to do</span>
            <h2 className="font-serif text-4xl md:text-6xl mt-4 text-white">Adventure by the river</h2>
          </div>
          <p className="text-white/70 max-w-md mt-4 md:mt-0">From a 100 m river zipline to mud volleyball and a scenic pool — there is something for every kind of traveller.</p>
        </div>

        {/* Bento */}
        <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[150px] md:auto-rows-[190px] gap-3 md:gap-4">
          <div className="col-span-2 row-span-2 relative rounded-lg overflow-hidden group">
            <LazyVideo src={VIDEOS.activity1} poster={ACTIVITIES[9].img} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12201a]/85 to-transparent" />
            <div className="absolute left-5 bottom-5 right-5">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-3"><Play size={20} className="text-white ml-0.5" /></div>
              <h3 className="font-serif text-2xl md:text-3xl text-white">The Riverside Experience</h3>
              <p className="text-white/75 text-sm mt-1">Play, splash and unwind where the river meets the estate.</p>
            </div>
          </div>
          <div className="relative rounded-lg overflow-hidden group">
            <LazyVideo src={VIDEOS.activity2} poster={ACTIVITIES[1].img} />
            <div className="absolute inset-0 bg-[#12201a]/35 group-hover:bg-[#12201a]/10 transition-colors" />
            <span className="absolute left-4 bottom-4 text-white font-medium text-sm">River Zipline</span>
          </div>
          <div className="relative rounded-lg overflow-hidden group">
            <img src={ACTIVITIES[0].img} alt="pool" loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-[#12201a]/30" />
            <span className="absolute left-4 bottom-4 text-white font-medium text-sm">Scenic Pool</span>
          </div>
          <div className="col-span-2 relative rounded-lg overflow-hidden group">
            <LazyVideo src={VIDEOS.activity3} poster={ACTIVITIES[3].img} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12201a]/80 to-transparent" />
            <span className="absolute left-4 bottom-4 text-white font-serif text-xl">Rain Dance &amp; Bonfire Nights</span>
          </div>
        </div>

        {/* Full list */}
        <div className="mt-10">
          <h3 className="text-white/60 text-xs tracking-[0.3em] uppercase mb-5">All Activities</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ACTIVITIES.map((a, i) => (
              <div key={i} className="flex gap-4 items-center bg-white/[0.04] border border-white/10 rounded-lg p-3 hover:bg-white/[0.08] transition-colors" data-testid={`activity-${i}`}>
                <img src={a.img} alt={a.title} loading="lazy" className="w-16 h-16 rounded-md object-cover flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">{a.title}</p>
                  <p className="text-white/60 text-sm leading-snug">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivitiesSection;
